import React from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Tooltip, Typography, Chip, Box, CircularProgress, Checkbox,
} from '@mui/material';
import { Edit, Delete, Scoreboard, DragIndicator } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { formatKickoff, teamLabel, hasFinalScore, scoringLabel, emptyWeekMessage, moveBlockReason } from './gameDisplay';

// The week's games, in the order players see them. Presentational: every
// action is a callback, and all state lives in GameManagement.
function GamesTable({
  games, teams, week, isLoading, onDragEnd, onEnterScores, onEdit, onDelete, selectedIds, onToggleSelect, onSelectAll,
}) {
  const columnCount = 8;

  const renderMessageRow = (content) => (
    <TableRow>
      <TableCell colSpan={columnCount} align="center" sx={{ py: 5, color: 'text.secondary' }}>
        {content}
      </TableCell>
    </TableRow>
  );

  return (
    <TableContainer component={Paper}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Table size="small" aria-label={`Week ${week} games`}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                {!isLoading && <SelectAllBox games={games} week={week} selectedIds={selectedIds} onSelectAll={onSelectAll} />}
              </TableCell>
              <TableCell sx={{ width: 72 }}>#</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Kickoff</TableCell>
              <TableCell>Matchup</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Spread</TableCell>
              <TableCell align="center">Score</TableCell>
              <TableCell>Scoring</TableCell>
              <TableCell align="right" sx={{ width: 150 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <Droppable droppableId="games">
            {(provided) => (
              <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                {isLoading && renderMessageRow(<CircularProgress size={24} aria-label="Loading games" />)}
                {!isLoading && games.length === 0 && renderMessageRow(emptyWeekMessage(week))}
                {!isLoading && games.map((game, index) => (
                  <Draggable key={game.id} draggableId={game.id} index={index}>
                    {(draggable, snapshot) => (
                      <TableRow
                        ref={draggable.innerRef}
                        {...draggable.draggableProps}
                        hover
                        selected={selectedIds.has(game.id)}
                        sx={{ bgcolor: snapshot.isDragging ? 'action.hover' : undefined }}
                      >
                        <TableCell padding="checkbox">
                          <SelectBox game={game} teams={teams} selectedIds={selectedIds} onToggleSelect={onToggleSelect} />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title="Drag to reorder">
                              <Box
                                component="span"
                                aria-label={`Drag to reorder ${teamLabel(teams, game.awayTeam)} at ${teamLabel(teams, game.homeTeam)}`}
                                {...draggable.dragHandleProps}
                                sx={{ display: 'inline-flex', cursor: 'grab', color: 'text.secondary' }}
                              >
                                <DragIndicator fontSize="small" />
                              </Box>
                            </Tooltip>
                            {index + 1}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.85em' }}>
                          {formatKickoff(game.startTime)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {teamLabel(teams, game.awayTeam, game.awayRanking)}
                            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                              {game.neutralSite ? ' vs ' : ' at '}
                            </Box>
                            {teamLabel(teams, game.homeTeam, game.homeRanking)}
                          </Typography>
                          {game.neutralSite && (
                            <Typography variant="caption" color="text.secondary">
                              Neutral site: {game.neutralSite}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.85em' }}>
                          {game.vegasLine || '—'}
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          {hasFinalScore(game)
                            ? <strong>{game.awayScore}–{game.homeScore}</strong>
                            : <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={scoringLabel(game)}
                            color={game.isPickScore ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Enter Scores">
                            <IconButton size="small" onClick={() => onEnterScores(game)} color="primary">
                              <Scoreboard fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Game Details">
                            <IconButton size="small" onClick={() => onEdit(game)} color="primary">
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Game">
                            <IconButton size="small" onClick={() => onDelete(game)} color="secondary">
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </Table>
      </DragDropContext>
    </TableContainer>
  );
}

// Ticks every game in the week that can move, or clears them all. Games that
// can't move (kicked off, scored) are never selected.
export function SelectAllBox({ games, week, selectedIds, onSelectAll }) {
  const movable = games.filter((g) => !moveBlockReason(g)).map((g) => g.id);
  const selectedCount = movable.filter((id) => selectedIds.has(id)).length;
  const all = movable.length > 0 && selectedCount === movable.length;
  const title = movable.length === 0
    ? 'No games in this week can be moved'
    : all ? 'Clear the selection' : `Select all ${movable.length} games that can move`;
  return (
    <Tooltip title={title}>
      <span>
        <Checkbox
          size="small"
          checked={all}
          indeterminate={selectedCount > 0 && !all}
          disabled={movable.length === 0}
          onChange={() => onSelectAll(all ? [] : movable)}
          slotProps={{ input: { 'aria-label': `Select all Week ${week} games` } }}
        />
      </span>
    </Tooltip>
  );
}

// Selects a game for "Move to Week". Games that can't move show why instead.
export function SelectBox({ game, teams, selectedIds, onToggleSelect }) {
  const blocked = moveBlockReason(game);
  const name = `${teamLabel(teams, game.awayTeam)} at ${teamLabel(teams, game.homeTeam)}`;
  return (
    <Tooltip title={blocked ? `Can't move: ${blocked.toLowerCase()}` : ''}>
      <span>
        <Checkbox
          size="small"
          checked={selectedIds.has(game.id)}
          disabled={Boolean(blocked)}
          onChange={() => onToggleSelect(game.id)}
          slotProps={{ input: { 'aria-label': `Select ${name}` } }}
        />
      </span>
    </Tooltip>
  );
}

export default GamesTable;
